import { useCreateDialogLookModal } from 'components/create-dialog/CreateDialogModalFrame';
import { Button } from 'components/ui/button';
import { ReactNode, useCallback } from 'react';
import { InvoiceData, InvoiceRowForm, InvoiceRowTotals } from 'types/invoices';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { InvoiceRowEditModal } from './InvoiceRowEditModal';
import { totalsButtonStyle } from '../TotalsTableCard';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    width: '700px',
};

interface Props {
    children: ReactNode;
    matterId: number;
    invoice: InvoiceData;
    handleSubmit: (data: InvoiceRowForm) => void;
    handleDelete: (invoiceRowId: number) => void;
    isDeleteBlocked: boolean;
    invoiceRowTotals: InvoiceRowTotals;
}

const useInvoiceRowEditModal = (
    invoice: InvoiceData,
    matterId: number,
    handleSubmit: (data: InvoiceRowForm) => void,
    handleDelete: (invoiceRowId: number) => void,
    isDeleteBlocked: boolean,
    invoiceRowTotals: InvoiceRowTotals
) => {
    const { openModal, closeModal } = useCreateDialogLookModal();
    const { isPaymentCurrencyMode } = usePaymentCurrencyMode(invoice);

    const openEditRowModal = useCallback(() => {
        const itemTotal = isPaymentCurrencyMode
            ? (invoiceRowTotals.itemTotalInPaymentCurrency ?? 0)
            : (invoiceRowTotals.itemTotal ?? 0);
        const amountBeforeDiscount = isPaymentCurrencyMode
            ? (invoiceRowTotals.amountBeforeDiscountInPaymentCurrency ?? 0)
            : (invoiceRowTotals.amountBeforeDiscount ?? 0);
        const discountAmount = isPaymentCurrencyMode
            ? amountBeforeDiscount - itemTotal
            : (invoiceRowTotals.discountAmount ?? 0);
        openModal(
            <InvoiceRowEditModal
                onClose={closeModal}
                action={'update'}
                invoice={invoice}
                invoiceRowTotals={invoiceRowTotals}
                initialData={{
                    invoiceRowId: invoiceRowTotals.invoiceRowId ?? 0,
                    matterId: matterId ?? 0,
                    description: invoiceRowTotals.description ?? '',
                    itemTotal,
                    discountRate: invoiceRowTotals.discountRate ?? 0,
                    discountAmount: discountAmount ? (Math.abs(discountAmount) * -1).toFixed(2) : '0',
                    amountBeforeDiscount,
                }}
                onSubmit={handleSubmit}
                onDelete={isDeleteBlocked ? undefined : handleDelete}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    }, [
        closeModal,
        handleDelete,
        handleSubmit,
        invoiceRowTotals,
        isDeleteBlocked,
        isPaymentCurrencyMode,
        invoice,
        matterId,
        openModal,
    ]);

    return { openEditRowModal };
};

export const InvoiceRowEditButton = (props: Props) => {
    const { handleSubmit, handleDelete, isDeleteBlocked, invoice, matterId, invoiceRowTotals, children } = props;

    const { openEditRowModal } = useInvoiceRowEditModal(
        invoice,
        matterId,
        handleSubmit,
        handleDelete,
        isDeleteBlocked,
        invoiceRowTotals
    );

    return (
        <Button {...totalsButtonStyle('w-[45px]')} onClick={openEditRowModal}>
            {children}
        </Button>
    );
};
